import { useEffect, useState } from "react";
import axios from "axios";
import { X, Music2 } from "lucide-react";
import LeadCaptureForm from "@/components/LeadCaptureForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "tmh_popup_closed";
const SESSION_KEY = "tmh_popup_shown";
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

export default function LeadPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const closed = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (closed && Date.now() - closed < SEVEN_DAYS) return undefined;
      if (sessionStorage.getItem(SESSION_KEY)) return undefined;
    } catch (e) {
      /* storage unavailable - popup still allowed */
    }

    let shown = false;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    const show = () => {
      if (shown) return;
      shown = true;
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch (e) {}
      setOpen(true);
      axios.post(`${API}/metrics/popup-view`).catch(() => {});
      clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) show();
    };
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      if (max > 0 && h.scrollTop / max >= 0.5) show();
    };
    const timer = setTimeout(show, 10000);
    if (isMobile) window.addEventListener("scroll", onScroll, { passive: true });
    else document.addEventListener("mouseout", onMouseOut);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {}
  };

  const handleSuccess = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {}
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-ink/60 backdrop-blur-sm"
      data-testid="lead-popup"
      onClick={dismiss}
    >
      <div
        className="relative w-full sm:max-w-md bg-paper rounded-t-3xl sm:rounded-3xl border-t-4 border-brand shadow-[0_-20px_60px_-20px_rgba(27,42,74,0.5)] p-6 sm:p-8 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          aria-label="Close popup"
          data-testid="lead-popup-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full border border-ink/10 bg-white flex items-center justify-center text-ink/60 hover:text-brand hover:border-brand/40 transition-colors"
        >
          <X size={16} />
        </button>
        <p className="text-[10px] tracking-[0.3em] text-brand font-bold inline-flex items-center gap-1.5">
          <Music2 size={12} /> SION EAST · MUMBAI
        </p>
        <h2 className="mt-2.5 pr-8 font-body font-extrabold text-2xl sm:text-3xl text-ink tracking-tight leading-tight" data-testid="lead-popup-headline">
          Book Your <span className="text-brand italic">2 Free Trial Classes</span>
        </h2>
        <p className="mt-2 text-sm text-muted" data-testid="lead-popup-subtext">
          Play first, pay later. No commitment.
        </p>
        <div className="mt-5">
          <LeadCaptureForm source="popup" onSuccess={handleSuccess} idPrefix="popup-form" />
        </div>
      </div>
    </div>
  );
}
